import exifr from 'exifr';

export type PreparedImage = {
  full: Blob;
  thumb: Blob;
  width: number;
  height: number;
  takenAt: string | null;
  latitude: number | null;
  longitude: number | null;
};

/** ТЗ 7.1: 1600 px по длинной стороне, миниатюра 400 px, WebP качества 82. */
const FULL_EDGE = 1600;
const THUMB_EDGE = 400;
const QUALITY = 0.82;

/**
 * EXIF читается ДО сжатия. Canvas метаданные не переносит: после отрисовки
 * ни даты съёмки, ни координат в файле уже нет, и восстановить их неоткуда
 * (ТЗ 7.1, шаг 2).
 */
async function readExif(file: File): Promise<{
  takenAt: string | null;
  latitude: number | null;
  longitude: number | null;
  orientation: number;
}> {
  try {
    const data: Record<string, unknown> | undefined = await exifr.parse(file, {
      tiff: true,
      exif: true,
      gps: true,
      pick: ['DateTimeOriginal', 'CreateDate', 'Orientation', 'latitude', 'longitude'],
    });
    if (!data) return { takenAt: null, latitude: null, longitude: null, orientation: 1 };

    const taken = (data.DateTimeOriginal ?? data.CreateDate) as Date | undefined;
    const latitude = typeof data.latitude === 'number' ? data.latitude : null;
    const longitude = typeof data.longitude === 'number' ? data.longitude : null;

    return {
      takenAt: taken instanceof Date && !Number.isNaN(taken.valueOf()) ? taken.toISOString() : null,
      latitude,
      longitude,
      orientation: typeof data.Orientation === 'number' ? data.Orientation : 1,
    };
  } catch {
    // Снимок без EXIF или с битым — не повод отказываться от загрузки.
    return { takenAt: null, latitude: null, longitude: null, orientation: 1 };
  }
}

/** Поддерживает ли браузер автоповорот при декодировании. */
let autoOrientSupported: boolean | null = null;
async function supportsAutoOrient(): Promise<boolean> {
  if (autoOrientSupported !== null) return autoOrientSupported;
  try {
    const probe = new Blob(
      [Uint8Array.from(atob('R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=='), (c) => c.charCodeAt(0))],
      { type: 'image/gif' },
    );
    await createImageBitmap(probe, { imageOrientation: 'from-image' });
    autoOrientSupported = true;
  } catch {
    autoOrientSupported = false;
  }
  return autoOrientSupported;
}

/**
 * Рисует картинку в canvas нужного размера, применяя поворот.
 *
 * Самая частая ошибка этого места: тег Orientation теряется при отрисовке на
 * canvas, и портретные снимки ложатся набок (ТЗ 7.4). Современные браузеры
 * умеют применить его сами при декодировании; где не умеют — применяем
 * матрицу вручную по прочитанному из EXIF значению.
 */
async function draw(
  source: ImageBitmap,
  maxEdge: number,
  orientation: number,
  applyManually: boolean,
): Promise<{ blob: Blob; width: number; height: number }> {
  // При повороте на 90° стороны меняются местами.
  const swapped = applyManually && orientation >= 5 && orientation <= 8;
  const sourceWidth = swapped ? source.height : source.width;
  const sourceHeight = swapped ? source.width : source.height;

  const scale = Math.min(1, maxEdge / Math.max(sourceWidth, sourceHeight));
  const width = Math.round(sourceWidth * scale);
  const height = Math.round(sourceHeight * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Не удалось подготовить изображение');

  if (applyManually && orientation > 1) {
    const w = width;
    const h = height;
    const transforms: Record<number, [number, number, number, number, number, number]> = {
      2: [-1, 0, 0, 1, w, 0],
      3: [-1, 0, 0, -1, w, h],
      4: [1, 0, 0, -1, 0, h],
      5: [0, 1, 1, 0, 0, 0],
      6: [0, 1, -1, 0, w, 0],
      7: [0, -1, -1, 0, w, h],
      8: [0, -1, 1, 0, 0, h],
    };
    const t = transforms[orientation];
    if (t) context.setTransform(...t);
  }

  context.drawImage(source, 0, 0, swapped ? height : width, swapped ? width : height);
  context.setTransform(1, 0, 0, 1, 0, 0);

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/webp', QUALITY),
  );
  // WebP поддерживают все целевые браузеры, но если вдруг нет — JPEG.
  const result =
    blob ??
    (await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', QUALITY),
    ));
  if (!result) throw new Error('Не удалось сжать изображение');

  return { blob: result, width, height };
}

/**
 * Снимок с телефона — это 4–8 МБ. После сжатия около 250 КБ: разница между
 * «влезает на годы» и «упирается в лимит за месяц» (ТЗ 7.2).
 */
export async function prepareImage(file: File): Promise<PreparedImage> {
  const exif = await readExif(file);
  const auto = await supportsAutoOrient();

  const bitmap = await createImageBitmap(file, auto ? { imageOrientation: 'from-image' } : {});
  try {
    const full = await draw(bitmap, FULL_EDGE, exif.orientation, !auto);
    const thumb = await draw(bitmap, THUMB_EDGE, exif.orientation, !auto);

    return {
      full: full.blob,
      thumb: thumb.blob,
      width: full.width,
      height: full.height,
      takenAt: exif.takenAt,
      latitude: exif.latitude,
      longitude: exif.longitude,
    };
  } finally {
    bitmap.close();
  }
}
